import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type FleetPresenceView, type FleetView, type MapConfigView, type OpenRideMapView } from '@vello/api-client';
import { AUTO_REFRESH_MS, Button, CheckRow, EmptyState, Field, KeyValueList, KpiGrid, ListTable, Notice, PageHeader, Panel, Row, Select, Split, Stack, StatCard, Text, TextInput, ToneDot, type ToneDotTone, FormRow, SectionTitle, MapMarkerTipLine, useAutoRefresh } from '@vello/web-theme';
import {
  PRESENCES,
  ageParts,
  driverById,
  fleetCategories,
  fleetMapPoints,
  formatMarketInstant,
  liveMapAuthority,
  manualDispatch,
  tileOverride,
  presenceTone,
  tailOf,
  type MapPoint,
} from '../../src/index';
import { PersonName, usePersonNames } from '../components/person-name';
import { MapCanvas } from '../components/map-canvas';
import { useAdmin } from '../session';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { LoadingBlock } from '../components/loading-block';
import { rowActions } from '../components/row-actions';

/**
 * Operações › Mapa ao vivo (W9-OPS-LIVE-MAP, D471, ADR-0089; DevMobility C-050, I-093; X-ray A-09): the market's drivers
 * on a geographic map with their presence (available, in ride, NO SIGNAL, offline), the rides waiting for a driver with
 * their pickup, filters (presence, category, a driver's plate/id), the counts, and — for an operator who holds the
 * dispatch words — the nearby candidates of a waiting ride, measured from ITS pickup (never a typed coordinate), with the
 * audited manual assignment.
 *
 *   - **The basemap is the platform's own** (D797): the api's vector map, the same as the native apps'; Integrations › Map
 *     tiles only overrides it with a raster source. The lists, the filters and the counts never depend on it.
 *   - **Fail closed on the market**: always one market's fleet, chosen from the operator's own grants, never typed.
 *   - **Honest presence**: "no signal" is a driver the server believes is on shift whose last sign of life is older than
 *     the market's window — never drawn as live. A last known position is drawn hollow.
 *   - Re-read on its own every `AUTO_REFRESH_MS.live` while the tab is visible (no refresh button, owner 2026-10-13).
 *   - **The map is the screen** (owner, 2026-10-18): every driver and every waiting ride is a pin; hovering (or focusing) a pin
 *     shows its details, selecting it opens the detail panel beside the map (for a waiting ride: the audited manual dispatch).
 *     There are NO tables under the map.
 *   - **Honest scope**: the feed carries drivers and rides only. Courier jobs (Send / food) have no coordinates in any read, so
 *     they are not drawn and the page subtitle says so (no "Serviço" filter over data that does not exist).
 */
/** The dot of a presence, by the same tone the old colour variable carried (available green, in ride ink, no signal amber, offline grey). */
const PRESENCE_DOT: Readonly<Record<FleetPresenceView, ToneDotTone>> = { AVAILABLE: 'success', IN_RIDE: 'ink', NO_SIGNAL: 'warning', OFFLINE: 'neutral' };

interface Candidate {
  readonly driverId: string;
}

/** The candidates route answers a list of driver ids; an object carrying a `driverId` is read too. */
function asCandidate(raw: unknown): Candidate | undefined {
  if (typeof raw === 'string' && raw !== '') return { driverId: raw };
  if (typeof raw !== 'object' || raw === null) return undefined;
  const c = raw as Record<string, unknown>;
  return typeof c.driverId === 'string' ? { driverId: c.driverId } : undefined;
}

export function LiveMapScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { v, fail, refusal: refusalOf } = useVocab();
  const authority = liveMapAuthority(grants);

  const { market, choices } = useScopeMarket();

  const [config, setConfig] = useState<MapConfigView | undefined>(undefined);
  const [fleet, setFleet] = useState<FleetView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [presence, setPresence] = useState<readonly FleetPresenceView[]>(PRESENCES);
  const [category, setCategory] = useState('');
  const [query, setQuery] = useState('');
  const [typed, setTyped] = useState('');
  const [selected, setSelected] = useState<{ readonly kind: MapPoint['kind']; readonly id: string } | undefined>(undefined);
  const [assigned, setAssigned] = useState<string | undefined>(undefined);
  const readSeq = useRef(0);

  // The search box applies after a short pause, never one request per key.
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(typed), 300);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const refuse = useCallback(
    (code: string, reasonCode: string | undefined, status?: number): string => {
      const text = refusalOf('refusal.admin.live_map', { code, reasonCode, status });
      return text ?? t('lmap.err.generic', { code: fail({ code, reasonCode }) });
    },
    [t, fail, refusalOf],
  );

  useEffect(() => {
    setConfig(undefined);
    setFleet(undefined);
    setSelected(undefined);
    setAssigned(undefined);
    setError(undefined);
    if (market === undefined) return undefined;
    let cancelled = false;
    void runtime.ops.mapConfig(market).then((res) => {
      if (cancelled) return;
      if (res.ok) setConfig(res.value);
      else setError(refuse(res.error.code, res.error.reasonCode, res.error.status));
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, market, refuse]);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (market === undefined) return;
    const res = await runtime.ops.fleet({ market, presence, ...(category !== '' ? { category } : {}), ...(query.trim() !== '' ? { q: query } : {}) });
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setFleet(res.value);
      setError(undefined);
    } else setError(refuse(res.error.code, res.error.reasonCode, res.error.status));
  }, [runtime, market, presence, category, query, refuse]);

  useEffect(() => {
    void load();
  }, [load]);

  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.live, enabled: market !== undefined });

  const timeZone = config?.market.timezone;
  const points = useMemo(() => fleetMapPoints(fleet), [fleet]);
  const categories = useMemo(() => fleetCategories(fleet), [fleet]);
  const driver = selected?.kind === 'driver' ? driverById(fleet, selected.id) : undefined;
  const selectedRide = selected?.kind === 'ride' ? fleet?.openRides.find((r) => r.rideId === selected.id) : undefined;
  const noPickupRides = (fleet?.openRides ?? []).filter((r) => r.pickup === undefined).length;
  const driversById = useMemo(() => new Map((fleet?.drivers ?? []).map((d) => [d.driverId, d] as const)), [fleet]);
  const driverName = usePersonNames((fleet?.drivers ?? []).map((d) => d.driverId));
  const ridesById = useMemo(() => new Map((fleet?.openRides ?? []).map((r) => [r.rideId, r] as const)), [fleet]);

  const togglePresence = (p: FleetPresenceView): void => setPresence((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  const ageText = (seconds: number | undefined): string => {
    const parts = ageParts(seconds);
    return parts === undefined ? '—' : t(`lmap.age.${parts.unit}`, { value: parts.value });
  };
  const pointLabel = (p: MapPoint): string => (p.kind === 'driver' ? t('lmap.pin.driver', { id: driverName(p.id) ?? tailOf(p.id) }) : t('lmap.pin.ride', { id: tailOf(p.id) }));

  /** The hover / focus bubble of a pin: a driver's situation, last signal, categories and current ride; a waiting ride's status, category and request time. */
  const pointTip = (p: MapPoint): ReactElement | undefined => {
    if (p.kind === 'driver') {
      const d = driversById.get(p.id);
      if (d === undefined) return undefined;
      return (
        <>
          <MapMarkerTipLine><strong><PersonName id={d.driverId} />{d.plate !== undefined ? ` · ${d.plate}` : ''}</strong></MapMarkerTipLine>
          <MapMarkerTipLine>{t('lmap.col.presence')}: {v('fleet.presence', d.presence)}</MapMarkerTipLine>
          <MapMarkerTipLine>{t('lmap.col.lastSeen')}: {ageText(d.lastSeenAgeSeconds)}</MapMarkerTipLine>
          <MapMarkerTipLine>{t('lmap.col.category')}: {d.categories.length > 0 ? d.categories.map((c) => v('ride.category', c)).join(', ') : '—'}</MapMarkerTipLine>
          <MapMarkerTipLine>{t('lmap.col.ride')}: {d.activeRideId !== undefined ? <span title={d.activeRideId}>{tailOf(d.activeRideId)}</span> : '—'}</MapMarkerTipLine>
        </>
      );
    }
    const r = ridesById.get(p.id);
    if (r === undefined) return undefined;
    return (
      <>
        <MapMarkerTipLine><strong>{t('lmap.pin.ride', { id: tailOf(r.rideId) })}</strong></MapMarkerTipLine>
        <MapMarkerTipLine>{t('lmap.col.status')}: {v('ride.status', r.status)}</MapMarkerTipLine>
        <MapMarkerTipLine>{t('lmap.col.category')}: {v('ride.category', r.categoryCode)}</MapMarkerTipLine>
        <MapMarkerTipLine>{t('lmap.col.requested')}: {formatMarketInstant(r.requestedAt, timeZone, lang)}</MapMarkerTipLine>
      </>
    );
  };

  const mapPanel = (
    <Panel>
      <Stack gap={3}>
      {config !== undefined ? (
        <MapCanvas
          tiles={tileOverride(config.tiles)}
          center={config.market.center}
          points={points}
          selectedId={selected?.id}
          onSelect={(p) => setSelected({ kind: p.kind, id: p.id })}
          ariaLabel={t('lmap.map.label')}
          pointLabel={pointLabel}
          pointTip={pointTip}
        />
      ) : (
        <LoadingBlock />
      )}
      <Row gap={4} wrap>
        {PRESENCES.map((p) => (
          <Row key={p} gap={2}>
            <ToneDot tone={PRESENCE_DOT[p]} />
            <Text role="caption" tone="secondary">{v('fleet.presence', p)}</Text>
          </Row>
        ))}
        <Text role="caption" tone="secondary">{t('lmap.legend.hollow')}</Text>
        <Text role="caption" tone="secondary">{t('lmap.legend.ride')}</Text>
        {fleet !== undefined && <Text role="caption" tone="secondary">{t('lmap.updatedAt', { time: formatMarketInstant(fleet.at, timeZone, lang, 'time') })}</Text>}
      </Row>
      {fleet?.truncated === true && <Notice tone="warning">{t('lmap.truncated', { shown: fleet.drivers.length, total: fleet.total })}</Notice>}
      {noPickupRides > 0 && <Notice tone="neutral">{t('lmap.rides.noPickupCount', { count: noPickupRides })}</Notice>}
      </Stack>
    </Panel>
    );

  const detailPanel =
    driver !== undefined ? (
      <Panel>
        <Stack gap={3}>
          <SectionTitle>{t('lmap.col.driver')} <PersonName id={driver.driverId} /></SectionTitle>
          <KeyValueList
            items={[
              { label: t('lmap.col.presence'), value: <>{v('fleet.presence', driver.presence)} <Text role="caption" tone="secondary">({v('fleet.presence_reason', driver.reasonCode)})</Text></> },
              ...(driver.plate !== undefined ? [{ label: t('lmap.detail.plate'), value: driver.plate, mono: true }] : []),
              { label: t('lmap.detail.position'), value: v('fleet.position_source', driver.positionSource) },
              { label: t('lmap.col.lastSeen'), value: ageText(driver.lastSeenAgeSeconds), mono: true },
              ...(driver.state !== undefined ? [{ label: t('lmap.detail.state'), value: v('driver.location_lifecycle_state', driver.state) }] : []),
              { label: t('lmap.col.category'), value: driver.categories.length > 0 ? driver.categories.map((c) => v('ride.category', c)).join(', ') : '—' },
              ...(driver.activeRideId !== undefined
                ? [{ label: t('lmap.col.ride'), value: <Button variant="ghost" onClick={() => navigate('ride_detail', { id: driver.activeRideId as string })}><span className="tnum" title={driver.activeRideId}>{tailOf(driver.activeRideId)}</span></Button> }]
                : []),
            ]}
          />
        </Stack>
      </Panel>
    ) : selectedRide !== undefined ? (
      <WaitingRidePanel key={selectedRide.rideId} ride={selectedRide} timeZone={timeZone} mayDispatch={authority.mayDispatch} onAssigned={(driverId) => { setAssigned(driverId); setSelected(undefined); void load(); }} />
    ) : null;

  return (
    <>
      <PageHeader title={t('lmap.title')} subtitle={t('lmap.subtitle')} />

      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {assigned !== undefined && <Notice tone="success">{withPerson(t('live.assigned', { driver: PERSON_SLOT }), PERSON_SLOT, assigned)}</Notice>}

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <>
          <KpiGrid>
            {PRESENCES.map((p) => (
              <StatCard key={p} label={v('fleet.presence', p)} value={fleet === undefined ? '—' : new Intl.NumberFormat(lang).format(fleet.summary[p])} tone={presenceTone(p)} />
            ))}
          </KpiGrid>

          <Panel>
            <Stack gap={3}>
              <Stack gap={3} role="group" aria-label={t('lmap.filters')}>
                <Row gap={4} wrap>
                  {PRESENCES.map((p) => (
                    <CheckRow key={p} id={`lmap-presence-${p}`} checked={presence.includes(p)} onChange={() => togglePresence(p)}>
                      {v('fleet.presence', p)}
                    </CheckRow>
                  ))}
                </Row>
                <FormRow columns="1fr 1fr">
                  <Field label={t('lmap.filter.category')} htmlFor="lmap-category">
                    <Select id="lmap-category" value={category} onChange={(e) => setCategory(e.target.value)}>
                      <option value="">{t('lmap.filter.allCategories')}</option>
                      {categories.map((c) => (
                        <option key={c} value={c}>{v('ride.category', c)}</option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={t('lmap.filter.search')} htmlFor="lmap-search">
                    <TextInput id="lmap-search" value={typed} onChange={(e) => setTyped(e.target.value)} />
                  </Field>
                </FormRow>
              </Stack>
              {presence.length === 0 && <Notice tone="warning">{t('lmap.filter.nonePresence')}</Notice>}
            </Stack>
          </Panel>

          {detailPanel !== null ? <Split ratio="2-1">{mapPanel}{detailPanel}</Split> : mapPanel}
        </>
      )}
    </>
  );
}

const PERSON_SLOT = '\u0001';

/** A person's name in the middle of a sentence: the `{driver}` slot of a message becomes the `PersonName` element. */
function withPerson(text: string, marker: string, id: string): ReactElement {
  const [before = '', after = ''] = text.split(marker);
  return <>{before}<PersonName id={id} />{after}</>;
}

/** The detail of a ride waiting for a driver; an operator with the dispatch words reads the nearby candidates (from ITS pickup) and assigns. */
function WaitingRidePanel({
  ride,
  timeZone,
  mayDispatch,
  onAssigned,
}: {
  readonly ride: OpenRideMapView;
  readonly timeZone: string | undefined;
  readonly mayDispatch: boolean;
  readonly onAssigned: (driverId: string) => void;
}): ReactElement {
  const { runtime } = useAdmin();
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { fail, v } = useVocab();
  const [candidates, setCandidates] = useState<readonly Candidate[] | undefined>(undefined);
  const [msg, setMsg] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);

  const findCandidates = async (): Promise<void> => {
    if (ride.pickup === undefined) return;
    setMsg(undefined);
    const res = await runtime.ops.candidates(ride.rideId, { latitude: ride.pickup.latitude, longitude: ride.pickup.longitude });
    if (res.ok) setCandidates(res.value.candidates.map(asCandidate).filter((c): c is Candidate => c !== undefined));
    else {
      setCandidates(undefined);
      setMsg({ tone: 'danger', text: t('live.error.candidates', { code: fail(res.error) }) });
    }
  };

  const assign = async (driverId: string): Promise<void> => {
    const res = await manualDispatch(runtime, ride.rideId, { driverId, reason: 'operator manual dispatch' });
    if (res.ok) onAssigned(driverId);
    else setMsg({ tone: 'danger', text: t('live.assignFailed', { code: fail(res.error) }) });
  };

  return (
    <Panel>
      <Stack gap={3}>
        <SectionTitle>{t('lmap.ride.title', { id: tailOf(ride.rideId) })}</SectionTitle>
        <KeyValueList
          items={[
            { label: t('lmap.col.status'), value: v('ride.status', ride.status) },
            ...(ride.categoryCode !== undefined ? [{ label: t('lmap.col.category'), value: v('ride.category', ride.categoryCode) }] : []),
            { label: t('lmap.col.requested'), value: formatMarketInstant(ride.requestedAt, timeZone, lang), mono: true },
          ]}
        />
        <Row gap={3} wrap>
          <Button variant="secondary" onClick={() => navigate('ride_detail', { id: ride.rideId })}>{t('lmap.ride.open')}</Button>
          {mayDispatch && ride.pickup !== undefined && <Button variant="secondary" onClick={() => void findCandidates()}>{t('lmap.rides.candidates')}</Button>}
        </Row>
        {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
        {candidates !== undefined && (
          <Stack gap={2}>
            <SectionTitle level={3}>{t('lmap.rides.candidatesTitle', { id: tailOf(ride.rideId) })}</SectionTitle>
            <ListTable
              {...rowActions([
                { key: 'driver', header: t('lmap.col.driver'), width: '1fr', render: (c: Candidate) => <PersonName id={c.driverId} /> },
                { key: 'act', header: '', width: '120px', render: (c: Candidate) => <Button variant="secondary" onClick={() => void assign(c.driverId)}>{t('live.assign')}</Button> },
              ])}
              rows={candidates}
              rowKey={(c) => c.driverId}
              empty={<EmptyState title={t('live.empty.candidates')} />}
              minWidth={260}
            />
          </Stack>
        )}
      </Stack>
    </Panel>
  );
}
